import './style.scss';

import { useMemo, useRef, useState, useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactRouterPrompt from 'react-router-prompt';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Menu } from 'primereact/menu';
import { DetailInfo, EvaluationForm, EvaluateTable } from 'features/AcceptanceReview/components';
import { HugerteEditor } from 'components/FormControl';

import {
  downloadDocxTemplate,
  downloadPdfTemplate,
  getReviewerInfo,
  createAcceptanceReview,
  getDetail,
} from 'apis/acceptanceReviews.api';

import ErrorDialog from 'components/ErrorDialog';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';

import { createAcceptanceReviewValidationSchema } from 'features/AcceptanceReview/validation';
import Signature from 'features/PrequalificationReview/components/Signature';
import DynamicAcceptanceForm from 'features/AcceptanceReview/components/DynamicAcceptanceReview';
import { is1593 } from 'utils/func';
import { validateGeneralInfoWithPayment } from 'utils/validation/generalInfo';

export default function AcceptanceReviewFormCreate() {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { fromInternal } = location.state || { fromInternal: false };
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);

  const { projectId, projectProposalId, summaryReportId } = useParams();
  const [searchParams] = useSearchParams();
  const councilMemberId = searchParams.get('councilMemberId');
  const userId = searchParams.get('userId');
  const councilId = searchParams.get('councilId');
  const downloadTemplateButtonRef = useRef();

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({ mode: 'onChange', resolver: yupResolver(createAcceptanceReviewValidationSchema) });

  const { isDirty, dirtyFields } = useFormState({ control });

  const {
    mutate,
    error: errorRes,
    isError: isErrorRes,
    isLoading: isLoadingCreate,
  } = useMutation(createAcceptanceReview);

  // A. General Information
  const {
    data: infoData,
    isLoading,
    refetch: refetchReviewerInfo,
  } = useQuery(['acceptance-reviewer', userId, councilId, projectProposalId], () =>
    getReviewerInfo(userId, projectProposalId, councilId, projectProposalId)
  );
  const info = useMemo(() => infoData?.data, [infoData]);

  const { data: acceptanceReviewData, isLoading: isLoadingAcceptanceReview } = useQuery(
    ['acceptance-review', councilMemberId, projectProposalId, councilId],
    () => getDetail(councilMemberId, projectProposalId, councilId),
    {
      enabled: !fromInternal,
    }
  );

  useEffect(() => {
    const researchTypeCode = info?.projectProposal?.researchType?.code;
    if (researchTypeCode) {
      setValue('score.researchTypeCode', researchTypeCode);
    }
  }, [info]);
  // #endregion

  // #region Events
  const onSubmit = async (data) => {
    // Kiểm tra thông tin chung (bao gồm cả payment info)
    const generalInfoError = validateGeneralInfoWithPayment(info?.projectProposal, info?.user, t);
    if (generalInfoError) {
      toast(TOAST.ERROR, generalInfoError);
      return;
    }

    // Kiểm tra chữ ký trước khi save
    if (!info?.user?.signatureUrl) {
      toast(TOAST.ERROR, t('council.review.signatureRequired'));
      return;
    }

    const reviewMeet = data?.reviewMeet;
    const reviewProduct = data?.reviewProduct;
    const reviewReportQuality = data?.reviewReportQuality;
    const reviewConclusion = data?.reviewConclusion;
    const scoreTarget = data?.score?.scoreTarget;
    const scoreContent = data?.score?.scoreContent;
    const scoreApproach = data?.score?.scoreApproach;
    const scoreProcess = data?.score?.scoreProcess;
    const scoreApplicationProduct = data?.score?.scoreApplicationProduct;
    const scorePublishedProduct = data?.score?.scorePublishedProduct;
    const scoreTrainingProduct = data?.score?.scoreTrainingProduct;
    const scoreReportQuality = data?.score?.scoreReportQuality;

    const submitData = {
      projectProposalId,
      councilId,
      councilMemberId,
      reviewMeet,
      reviewProduct,
      reviewReportQuality,
      reviewConclusion,
      scoreTarget,
      scoreContent,
      scoreApproach,
      scoreProcess,
      scoreApplicationProduct,
      scorePublishedProduct,
      scoreTrainingProduct,
      scoreReportQuality,
    };

    mutate(submitData, {
      onSuccess: () => {
        reset({}, { keepValues: true });
        toast(TOAST.SUCCESS, t('council.review.createSuccess'));
        setTimeout(() => {
          const queryParams = `councilMemberId=${councilMemberId}&userId=${userId}&councilId=${councilId}`;

          navigate(
            `/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/acceptance-document/acceptance-review/edit?${queryParams}`,
            { replace: true }
          );
        }, 1500);
      },
      onError: () => {
        setIsVisibleDialogError(true);
      },
    });
  };

  const {
    refetch: handleDownloadPdfTemplate,
    isLoading: isDownloadPdfTemplateLoading,
    isFetching: isDownloadPdfTemplateFetching,
  } = useQuery(
    ['downloadPdfTemplateAcceptanceReview', projectProposalId, councilId, councilMemberId],
    () =>
      downloadPdfTemplate({
        projectProposalId,
        referenceId: projectProposalId,
        councilId,
        councilMemberId,
      }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Mau M08 - Phieu nhan xet danh gia nghiem thu.pdf';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
      },
    }
  );

  const {
    refetch: handleDownloadDocxTemplate,
    isLoading: isDownloadDocxTemplateLoading,
    isFetching: isDownloadDocxTemplateFetching,
  } = useQuery(
    ['downloadDocxTemplateAcceptanceReview', projectProposalId, councilId, councilMemberId],
    () =>
      downloadDocxTemplate({
        projectProposalId,
        referenceId: projectProposalId,
        councilId,
        councilMemberId,
      }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Mau M08 - Phieu nhan xet danh gia nghiem thu.docx';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportDocxErrorTitle'));
      },
    }
  );

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  const template = useMemo(() => info?.projectProposal?.template, [info]);

  const renderFirstTab = () => {
    if (is1593(template) || isLoading) {
      return (
        <DynamicAcceptanceForm
          projectProposalId={projectProposalId}
          councilId={councilId}
          councilMemberId={councilMemberId}
          generalInfo={info?.projectProposal}
          isLoading={isLoading}
        />
      );
    }
    return (
      <div className="m-0 bg-white px-4 sm:px-4 md:px-4 lg:px-6 xl:px-8 mb-3 py-5 p-fluid relative financial-review-create">
        <div className="flex gap-1 justify-content-end">
          <Button
            // type="button"
            label={t('council.review.downloadTemplate')}
            icon={`pi ${
              isDownloadPdfTemplateLoading ||
              isDownloadPdfTemplateFetching ||
              isDownloadDocxTemplateLoading ||
              isDownloadDocxTemplateFetching
                ? 'pi-spin pi-spinner'
                : 'pi-file'
            }`}
            className="block w-auto h-3rem border-none"
            severity="info"
            onClick={(event) => downloadTemplateButtonRef.current.toggle(event)}
            disabled={
              isDownloadPdfTemplateLoading ||
              isDownloadPdfTemplateFetching ||
              isDownloadDocxTemplateLoading ||
              isDownloadDocxTemplateFetching
            }
          />
        </div>
        <Menu
          ref={downloadTemplateButtonRef}
          model={[
            {
              label: t('council.review.downloadPdfTemplate'),
              icon: 'pi pi-file-pdf',
              command: handleDownloadPdfTemplate,
              disabled: isDownloadPdfTemplateLoading || isDownloadPdfTemplateFetching,
            },
            {
              label: t('council.review.downloadDocxTemplate'),
              icon: 'pi pi-file-word',
              command: handleDownloadDocxTemplate,
              disabled: isDownloadDocxTemplateLoading || isDownloadDocxTemplateFetching,
            },
          ]}
          popup
        />

        <div className="mb-5 flex justify-content-between align-items-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="VNU-HCM logo"
            className="ml-3 h-4rem md:h-5rem"
          />
          <div className="pl-2">
            {t('projectProposal.viewDetail.form')} M08 <br />
            {t('council.m08.projectCode')}
          </div>
        </div>
        <div className="text-center mb-6">
          <h3 className="font-bold mb-0">{t('council.m08.title')}</h3>
          <h3 className="mt-0">{t('council.m08.sciTechProject')}</h3>
        </div>

        <DetailInfo info={info} />

        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <EvaluationForm
            watch={watch}
            setValue={setValue}
            getValues={getValues}
            control={control}
            errors={errors}
          />

          <EvaluateTable
            setValue={setValue}
            getValues={getValues}
            watch={watch}
            control={control}
            errors={errors}
            researchType={info?.projectProposal?.researchType}
          />

          <h5 className="font-bold">{t('council.m08.b.rank.label')}</h5>
          <ul>
            <li>{t('council.m08.b.rank.excellent')}</li>
            <li>{t('council.m08.b.rank.veryGood')}</li>
            <li>{t('council.m08.b.rank.good')}</li>
            <li>{t('council.m08.b.rank.reached')}</li>
            <li>{t('council.m08.b.rank.notReached')}</li>
          </ul>
          <div className="text-lg mt-3">
            <h5>{t('council.m08.c.conclusion')}</h5>
            <HugerteEditor name="reviewConclusion" control={control} errors={errors} />
          </div>
          <div className="grid text-lg mt-3">
            <div className="col-7 px-8">
              <span>{t('council.m01.c.commitment')}</span>
            </div>
            <div className="col-5 text-center">
              <Signature refetchReviewerInfo={refetchReviewerInfo} reviewerInfo={info?.user} />
            </div>
          </div>

          <div className="flex justify-content-end mt-4">
            <Button
              label={t('formLayout.action.close')}
              severity="danger"
              type="button"
              className="mr-2 w-8rem"
              onClick={() => navigate(-1)}
            />
            <Button
              label={t('formLayout.action.save')}
              severity="info"
              type="submit"
              className="w-8rem"
              onClick={() => {
                if (Object.keys(errors || {}).length) {
                  toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
                }
              }}
              disabled={!(isDirty && Object.keys(dirtyFields).length) || isLoadingCreate}
            />
          </div>
        </form>
      </div>
    );
  };

  if (!isLoadingAcceptanceReview && acceptanceReviewData?.data?.acceptanceReview) {
    const queryParams = `councilMemberId=${councilMemberId}&userId=${userId}&councilId=${councilId}`;

    navigate(
      `/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/acceptance-document/acceptance-review/edit?${queryParams}`,
      { replace: true }
    );
  }
  // #endregion

  return (
    <>
      {isLoadingCreate && <Loading />}

      {isErrorRes && (
        <ErrorDialog
          title={t('council.review.create')}
          isError={isErrorRes}
          error={errorRes}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      <ReactRouterPrompt when={isDirty && Object.keys(dirtyFields).length}>
        {({ isActive, onConfirm, onCancel }) => (
          <Dialog
            visible={isActive}
            header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
            position="center"
            style={{ width: '300px' }}
            onHide={onCancel}
            draggable={false}
            footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
          >
            <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
          </Dialog>
        )}
      </ReactRouterPrompt>
      {renderFirstTab()}
    </>
  );
}
